import React from 'react';
import { useTranslation } from 'react-i18next';
import { AxiosResponse } from 'axios';
import { useFormContext } from 'react-hook-form';
import {
	CONSTANTS,
	CONTRACT_ACCEPT_TYPES,
	CONTRACT_ACCEPT_TYPES_LABELS,
} from 'utils';
import {
	deleteFileFromDB,
	uploadFileToDB,
	formatBytes,
	getUploadedFileName,
} from 'helpers';
import { PdfIcon } from 'components/icons';
import { IContract, IFile } from 'modules/admin-panel/models';
import { IClientFormFields } from 'modules/admin-panel/pages/clients/models';
import { Dropzone, SingleUpload } from 'components/drop-zone/Dropzone';

const contractPath = '/clients/file/contract';

interface ContractDropzoneProps {
	wrapperClass?: string;
	onUploadFail: (e: Error) => void;
	required?: boolean;
	onUpload: (name: string, logo?: IFile) => void;
	uploadedData?: IFile;
}

export function ContractDropzone({
	wrapperClass,
	onUploadFail,
	required,
	onUpload,
	uploadedData,
}: ContractDropzoneProps) {
	const { t } = useTranslation('common');
	const { setValue } = useFormContext();

	const handleUploadContract = (pdf: File) => {
		uploadFileToDB(pdf, contractPath)
			.then((res: AxiosResponse<IContract>) => onChangeContract(res.data))
			.catch((e) => {
				onChangeContract();
				onUploadFail(e);
			});
	};

	const handleDeleteContract = (uploadedContract: IFile) => {
		deleteFileFromDB(CONSTANTS.deleteFilePath, uploadedContract)
			.then(() => onChangeContract())
			.catch((e) => onUploadFail(e));
	};

	const renderUploadedData = (uploadedContract: IFile) => (
		<>
			<PdfIcon iconClass="dropzone__icon-file" />
			<div className="dropzone__text-uploaded">
				<p
					className="dropzone__name-uploaded"
					title={getUploadedFileName(uploadedContract.fileKey)}
				>
					{`${t('pdf_contract_file')} `}
					{getUploadedFileName(uploadedContract.fileKey)}
				</p>
				<p className="dropzone__size-uploaded">
					{formatBytes(uploadedContract.size)}
				</p>
			</div>
		</>
	);

	function onChangeContract(newContract?: IFile) {
		setValue(IClientFormFields.contract, newContract?.fileKey);
		onUpload(IClientFormFields.contract, newContract);
	}

	return (
		<Dropzone<SingleUpload<IFile>>
			id={IClientFormFields.contract}
			wrapperClass={wrapperClass}
			renderUploadedData={renderUploadedData}
			acceptTypes={CONTRACT_ACCEPT_TYPES}
			acceptTypesLabels={CONTRACT_ACCEPT_TYPES_LABELS}
			onUpload={handleUploadContract}
			onDeleteUploadedData={handleDeleteContract}
			uploadedData={uploadedData}
			required={required}
			title={t('dropzone.text_file')}
		/>
	);
}
